import React, {
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AgGridReact } from 'ag-grid-react';

import {
  EMBRYOS_PER_STRAW_VALUE,
  EMBRYOS_PER_VIT_STRAW_VALUE,
} from '../utils/constants';
import { FreezeQCForm } from '../utils/schema';
import { FreezeGradingRow } from '../utils/types';

interface DetailGridRefs {
  [fertId: string]: {
    liveEmbryosGridRef: React.RefObject<AgGridReact>;
    tempEmbryosGridRef: React.RefObject<AgGridReact>;
  };
}

export interface FreezeFormState {
  storageLocation?: string;
  freezeDay?: string;
  predictionTech?: string;
  grader?: string;
  freezeTech?: string;
  freezer?: string;
  embryosPerStraw?: number;
  embryosPerVitStraw?: number;
  dt?: boolean;
  freezeLocation?: string;
}

export interface FreezeContextState {
  showFreezeToolbar: boolean;
  setShowFreezeToolbar: (v: boolean) => void;
  showFreezeGrading: boolean;
  setShowFreezeGrading: (v: boolean) => void;
  showFreezeQC: boolean;
  setShowFreezeQC: (v: boolean) => void;
  showFreezeValidation: boolean;
  setShowFreezeValidation: (v: boolean) => void;
  showFreezeMetrics: boolean;
  setShowFreezeMetrics: (v: boolean) => void;
  showEditView: boolean;
  setShowEditView: (v: boolean) => void;
  allTempEmbryosSelected: boolean;
  setAllTempEmbryosSelected: (v: boolean) => void;
  allLiveEmbryosSelected: boolean;
  setAllLiveEmbryosSelected: (v: boolean) => void;
  isUpdateRunning: boolean;
  setIsUpdateRunning: (v: boolean) => void;
  isGridsExpanded: boolean;
  setIsGridsExpanded: (v: boolean) => void;
  form: FreezeFormState;
  setForm: React.Dispatch<React.SetStateAction<FreezeFormState>>;
  qcForm: FreezeQCForm;
  setQcForm: React.Dispatch<React.SetStateAction<FreezeQCForm>>;
  gradingGridData: FreezeGradingRow[] | undefined;
  setGradingGridData: React.Dispatch<
    React.SetStateAction<FreezeGradingRow[] | undefined>
  >;
  gradingGridKey: number;
  setGradingGridKey: React.Dispatch<React.SetStateAction<number>>;
  detailGridRefs: DetailGridRefs;
  registerGridRefs: (
    fertId: string,
    liveRef: React.RefObject<AgGridReact>,
    tempRef: React.RefObject<AgGridReact>,
  ) => void;
  unregisterGridRefs: (fertId: string) => void;
  gradingGridRef: React.RefObject<AgGridReact>;
}

export const FreezeContext = createContext<FreezeContextState | undefined>(
  undefined,
);

export const FreezeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [showFreezeToolbar, setShowFreezeToolbar] = useState(false);
  const [showFreezeGrading, setShowFreezeGrading] = useState(false);
  const [showFreezeQC, setShowFreezeQC] = useState(false);
  const [showFreezeValidation, setShowFreezeValidation] = useState(false);
  const [showFreezeMetrics, setShowFreezeMetrics] = useState(false);
  const [showEditView, setShowEditView] = useState(false);
  const [allTempEmbryosSelected, setAllTempEmbryosSelected] = useState(false);
  const [allLiveEmbryosSelected, setAllLiveEmbryosSelected] = useState(false);
  const [isUpdateRunning, setIsUpdateRunning] = useState(false);
  const [isGridsExpanded, setIsGridsExpanded] = useState(false);
  const [gradingGridData, setGradingGridData] = useState<FreezeGradingRow[]>();
  const [gradingGridKey, setGradingGridKey] = useState(0);
  const detailGridRefsRef = useRef<DetailGridRefs>({});
  const gradingGridRef = useRef<AgGridReact>(null);

  const [form, setForm] = useState<FreezeFormState>({
    storageLocation: '',
    freezeDay: undefined,
    predictionTech: undefined,
    grader: undefined,
    freezeTech: undefined,
    freezer: undefined,
    embryosPerVitStraw: EMBRYOS_PER_VIT_STRAW_VALUE,
    embryosPerStraw: EMBRYOS_PER_STRAW_VALUE,
    dt: true,
    freezeLocation: undefined,
  });

  const [qcForm, setQcForm] = useState<FreezeQCForm>({
    qcEg10: '',
    qcTryPsInDate: undefined,
    qcehmDate: undefined,
    equilibrationSolutionDate: undefined,
    qcEg10Date: undefined,
    qcSucroseDate: undefined,
    vitrificationSolutionDate: undefined,
    qcfreezingComment: '',
    qcStrawLotNumber: undefined,
    qcVitStrawLotNumber: undefined,
    qcSterileStrawLotNumber: undefined,
    qc12WellPlates: undefined,
  });

  const qcFormValue = useMemo(() => ({ qcForm, setQcForm }), [qcForm]);

  const formValue = useMemo(() => ({ form, setForm }), [form]);

  const selectionValue = useMemo(
    () => ({
      allTempEmbryosSelected,
      setAllTempEmbryosSelected,
      allLiveEmbryosSelected,
      setAllLiveEmbryosSelected,
    }),
    [allTempEmbryosSelected, allLiveEmbryosSelected],
  );

  const viewStateValue = useMemo(
    () => ({
      showFreezeToolbar,
      setShowFreezeToolbar,
      showFreezeGrading,
      setShowFreezeGrading,
      showFreezeQC,
      setShowFreezeQC,
      showFreezeValidation,
      setShowFreezeValidation,
      showFreezeMetrics,
      setShowFreezeMetrics,
      showEditView,
      setShowEditView,
    }),
    [
      showFreezeToolbar,
      showFreezeGrading,
      showFreezeQC,
      showFreezeValidation,
      showFreezeMetrics,
      showEditView,
    ],
  );

  const registerGridRefs = useCallback(
    (
      fertId: string,
      liveRef: React.RefObject<AgGridReact>,
      tempRef: React.RefObject<AgGridReact>,
    ) => {
      detailGridRefsRef.current[fertId] = {
        liveEmbryosGridRef: liveRef,
        tempEmbryosGridRef: tempRef,
      };
    },
    [],
  );

  const unregisterGridRefs = useCallback((fertId: string) => {
    delete detailGridRefsRef.current[fertId];
  }, []);

  const value = useMemo(
    () => ({
      ...viewStateValue,
      ...selectionValue,
      ...formValue,
      ...qcFormValue,
      isUpdateRunning,
      setIsUpdateRunning,
      isGridsExpanded,
      setIsGridsExpanded,
      gradingGridData,
      setGradingGridData,
      gradingGridKey,
      setGradingGridKey,
      detailGridRefs: detailGridRefsRef.current,
      registerGridRefs,
      unregisterGridRefs,
      gradingGridRef,
    }),
    [
      viewStateValue,
      selectionValue,
      formValue,
      qcFormValue,
      isUpdateRunning,
      isGridsExpanded,
      gradingGridData,
      setGradingGridData,
      gradingGridKey,
      setGradingGridKey,
      detailGridRefsRef,
      registerGridRefs,
      unregisterGridRefs,
      gradingGridRef,
    ],
  );

  return (
    <FreezeContext.Provider value={value}>{children}</FreezeContext.Provider>
  );
};
